/**
 * ScreensaverView - Fullscreen screensaver/photo frame
 * 
 * Features:
 * - Large clock display
 * - Date and weekday
 * - Optional photo slideshow
 * - Tap anywhere to exit
 * - Smooth fade transitions
 * - High z-index for reliable display on iOS
 * 
 * CRITICAL: Fixed positioning with z-index 9999 for iOS Safari
 */
import React, { useState, useEffect } from 'react';
import { useLiveClock } from '../../hooks/useLiveClock';

export default function ScreensaverView({ active = true, settings = {}, photos = [], onExit }) {
  const { timeString, dateString } = useLiveClock({ withSeconds: false, locale: 'de-DE' });
  
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [fade, setFade] = useState(true);

  // Don't render if not active
  if (!active) {
    return null;
  }

  // Photo slideshow logic
  useEffect(() => {
    if (!photos || photos.length === 0) return;

    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrentPhotoIndex(prev => (prev + 1) % photos.length);
        setFade(true);
      }, 500); // Fade out duration
    }, 20000); // Change photo every 20 seconds

    return () => clearInterval(interval);
  }, [photos]);

  const currentPhoto = photos && photos.length > 0 ? photos[currentPhotoIndex] : null;

  const handleExit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('[ScreensaverView] Exit triggered');
    if (onExit) {
      onExit();
    }
  };

  return (
    <div
      onClick={handleExit}
      onTouchStart={handleExit}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        zIndex: 9999,
        backgroundImage: currentPhoto ? `url(${currentPhoto})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent'
      }}
      className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center"
    >
      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-black/40" style={{ pointerEvents: 'none' }}></div>

      {/* Content */}
      <div className="relative z-10 text-center px-8" style={{ pointerEvents: 'none' }}>
        {/* Clock */}
        <div className="mb-6">
          <div className="text-9xl md:text-[12rem] font-bold text-white drop-shadow-2xl tracking-wider" style={{
            textShadow: '0 0 40px rgba(0,0,0,0.8)'
          }}>
            {timeString}
          </div>
        </div>

        {/* Date */}
        <div className="text-4xl md:text-5xl font-semibold text-white/90 drop-shadow-lg" style={{
          textShadow: '0 0 20px rgba(0,0,0,0.6)'
        }}>
          {dateString}
        </div>
      </div>

      {/* Tap anywhere hint (fades after 3 seconds) */}
      <div className="absolute bottom-8 text-white/60 text-xl animate-pulse" style={{
        pointerEvents: 'none',
        textShadow: '0 0 10px rgba(0,0,0,0.8)'
      }}>
        Tippen um fortzufahren
      </div>
    </div>
  );
}
